import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import { categories } from '@/mocks/categories';
import { blogArticles } from '@/mocks/blog';

const siteUrl =
  typeof __VITE_SITE_URL__ !== 'undefined' && __VITE_SITE_URL__
    ? __VITE_SITE_URL__.replace(/\/+$/, '')
    : 'https://mulebuyspreadsheets.motorcycles';

export default function ShoppingGuidePage() {
  return (
    <div className="min-h-screen bg-background-50">
      <SEO
        title="Mulebuy Spreadsheet Shopping Guide – Browse All Product Categories"
        description="Complete mulebuy spreadsheet shopping guide covering all 14 product categories including Shoes, Hoodies, T-Shirts, Jackets, Accessories, and more. Find curated product sourcing resources and essential guides for cross-border e-commerce."
        keywords="mulebuy spreadsheet shopping guide, mulebuy spreadsheet categories, mulebuy spreadsheet products, mulebuy spreadsheet sourcing"
        canonicalPath="/shopping-guide"
        ogType="website"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Mulebuy Spreadsheet Shopping Guide',
          description: 'Complete mulebuy spreadsheet shopping guide covering all 14 product categories with curated product sourcing resources and essential guides.',
          url: `${siteUrl}/shopping-guide`,
          isPartOf: {
            '@type': 'WebSite',
            name: 'Mulebuy Spreadsheet Database',
            url: siteUrl,
          },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: siteUrl,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Shopping Guide',
                item: `${siteUrl}/shopping-guide`,
              },
            ],
          },
        }}
      />
      <Navbar />
      <main>
        <section className="w-full pt-32 pb-16 md:pb-20">
          <div className="max-w-5xl mx-auto px-6 md:px-10">
            <div className="mb-8">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-foreground-950 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Mulebuy Spreadsheet
              </Link>
            </div>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
              Mulebuy Spreadsheet Shopping Guide
            </h1>
            <p className="text-foreground-600 text-base md:text-lg max-w-2xl leading-relaxed mb-12">
              Your complete guide to navigating the <strong>mulebuy spreadsheet</strong> product categories and finding exactly what you need. Browse by category below or explore our curated guides.
            </p>

            <div className="mb-16">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
                Product Categories
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-100 hover:bg-accent-50 border border-background-200 hover:border-accent-300 transition-all cursor-pointer group"
                  >
                    <span className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center group-hover:bg-accent-200 transition-colors">
                      <i className={`${cat.icon} text-accent-700 text-lg`}></i>
                    </span>
                    <span className="text-xs font-medium text-foreground-700 text-center leading-tight">
                      {cat.name}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            <div className="mb-16">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
                Essential Guides
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {blogArticles.slice(0, 6).map((article) => (
                  <Link
                    key={article.slug}
                    to={`/blog/${article.slug}`}
                    className="group bg-background-100 rounded-xl p-5 border border-background-200 hover:border-primary-300 transition-all cursor-pointer"
                  >
                    <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-label mb-3 inline-block">
                      {article.category}
                    </span>
                    <h3 className="font-heading text-base font-bold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                    <span className="text-xs text-foreground-400">{article.readTime}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="bg-primary-50 rounded-2xl p-8 md:p-10 text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-primary-900 mb-4">
                Ready to Start Exploring?
              </h2>
              <p className="text-primary-700 text-sm md:text-base max-w-lg mx-auto leading-relaxed mb-6">
                Visit the main kaspreadsheet.com platform to access the full mulebuy spreadsheet database with thousands of curated product listings.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Mulebuy Spreadsheet
                  <i className="ri-arrow-right-line"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-primary-300 text-primary-700 text-sm font-semibold hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Back to Home
                  <i className="ri-home-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}